import React, { useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { 
  ShoppingBag, Coffee, Clock, MapPin, Phone, 
  Instagram, MessageCircle, ArrowRight, X, Star, Heart, 
  ChefHat, Truck, Sparkles, ChevronRight,
  Plus
} from 'lucide-react';

const BasicBakeryLanding = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" as const } }
  };

  return (
    <div className="bg-[#FFFCF7] text-stone-800 min-h-screen font-sans selection:bg-orange-200 selection:text-orange-900 overflow-x-hidden">
      
      {/* === Warm Navbar === */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-orange-100 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer">
            <div className="bg-orange-500 p-2 rounded-xl text-white shadow-lg shadow-orange-200">
              <ChefHat size={26} />
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-2xl font-black text-stone-900 tracking-tight leading-none uppercase">Crust & <span className="text-orange-600">Cream</span></span>
              <span className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.3em] mt-1">Premium Artisan Bakery</span>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-10 text-[11px] font-black tracking-widest uppercase text-stone-500">
            <a href="#speciality" className="hover:text-orange-600 transition-colors">Cakes</a>
            <a href="#menu" className="hover:text-orange-600 transition-colors">Daily Bakes</a>
            <a href="#delivery" className="hover:text-orange-600 transition-colors">Delivery</a>
          </div>

          <div className="flex items-center gap-4">
            <a href="https://wa.me/910000000000" className="hidden md:flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest uppercase hover:shadow-xl transition-all">
              <MessageCircle size={16} /> WhatsApp Order
            </a>
            <button onClick={() => setIsFormOpen(true)} className="bg-stone-900 text-white p-2.5 rounded-full hover:bg-orange-600 transition-all relative">
              <ShoppingBag size={20} />
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[8px] w-4 h-4 rounded-full flex items-center justify-center font-bold border-2 border-white">0</span>
            </button>
          </div>
        </div>
      </nav>

      {/* === Huge Hero Section === */}
      <section className="relative pt-40 pb-24 px-6 lg:px-12 min-h-screen flex items-center justify-center overflow-hidden">
        {/* Decorative Background Doughnut Shape */}
        <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-orange-100/50 rounded-full blur-[120px] -z-10"></div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-5 py-2 rounded-full text-[11px] font-black tracking-widest uppercase mb-8 shadow-sm">
              <Sparkles size={14} /> Oven Fresh · Delivered in 60 Mins
            </div>
            <h1 className="text-6xl md:text-8xl font-serif text-stone-900 mb-8 leading-[0.95] tracking-tighter">
              Baking <br/> Your <span className="italic text-orange-600">Dreams</span> <br/> to Life.
            </h1>
            <p className="text-stone-500 font-light text-xl mb-12 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              From celebration cakes to morning sourdough, we use heritage recipes and local ingredients to create magic.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start">
              <button onClick={() => setIsFormOpen(true)} className="w-full sm:w-auto bg-orange-600 text-white px-10 py-5 rounded-2xl font-black tracking-widest uppercase hover:bg-stone-900 transition-all flex items-center justify-center gap-3 shadow-2xl shadow-orange-200">
                Order Your Cake <ArrowRight size={20} />
              </button>
              <div className="flex items-center gap-4 py-2">
                 <div className="flex -space-x-3">
                   {[1,2,3].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-stone-200 overflow-hidden"><img src={`https://i.pravatar.cc/100?img=${i+10}`} alt="user" /></div>)}
                 </div>
                 <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">Join 2k+ Happy Foodies</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }} className="relative">
            <div className="relative z-10 rounded-[40px] overflow-hidden shadow-2xl border-[15px] border-white rotate-2 hover:rotate-0 transition-transform duration-700">
              <img 
                src="https://tse2.mm.bing.net/th/id/OIP.vF6A0sOEdjJgHCjZhFpbgAHaFj?pid=Api&P=0&h=180" 
                alt="Signature Cake" 
                className="w-full h-[650px] object-cover"
              />
            </div>
            {/* Floating Review Card */}
            <div className="absolute -bottom-10 -right-10 bg-white p-6 rounded-3xl shadow-2xl z-20 max-w-[220px] border border-orange-50">
               <div className="flex gap-1 text-orange-500 mb-3">
                 {[1,2,3,4,5].map(s => <Star key={s} size={14} fill="currentColor" />)}
               </div>
               <p className="text-stone-700 text-sm italic mb-3">"The best Red Velvet cake I've ever had in the city!"</p>
               <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">- Riya Sharma</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Designer Cakes Section (New & Large) === */}
      <section id="speciality" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20">
            <div className="max-w-2xl">
              <span className="text-orange-500 font-black tracking-[0.3em] text-[10px] uppercase mb-4 block">Celebration Ready</span>
              <h2 className="text-4xl md:text-6xl font-serif text-stone-900 leading-none">Designer <span className="italic text-orange-600">Cakes</span></h2>
            </div>
            <p className="text-stone-400 text-sm font-medium mt-6 md:mt-0 max-w-xs">Customized themes for birthdays, weddings, and every special milestone.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { name: "Pink Floral Dream", type: "Wedding Specialist", img: "https://images.pexels.com/photos/1702373/pexels-photo-1702373.jpeg?auto=compress&cs=tinysrgb&w=800" },
              { name: "Chocolate Overload", type: "Birthday Bestseller", img: "https://images.pexels.com/photos/4109998/pexels-photo-4109998.jpeg?auto=compress&cs=tinysrgb&w=800" },
              { name: "Minimalist Bento", type: "Daily Celebration", img: "https://images.pexels.com/photos/1407346/pexels-photo-1407346.jpeg?auto=compress&cs=tinysrgb&w=800" }
            ].map((cake, i) => (
              <div key={i} className="group cursor-pointer">
                <div className="relative h-[500px] rounded-[32px] overflow-hidden mb-6 bg-stone-100">
                  <img src={cake.img} alt={cake.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-8">
                     <button onClick={() => setIsFormOpen(true)} className="w-full bg-white text-stone-900 py-4 rounded-xl font-black uppercase text-[10px] tracking-widest">Inquire Price</button>
                  </div>
                </div>
                <h3 className="text-2xl font-serif text-stone-900 mb-1">{cake.name}</h3>
                <p className="text-orange-600 text-xs font-bold uppercase tracking-widest">{cake.type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Why Our Bakery (Expanded Section) === */}
      <section className="py-32 bg-[#FAF7F2] px-6 lg:px-12 border-y border-orange-100/50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16">
          <div className="text-center md:text-left">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center text-orange-600 shadow-xl mb-8 mx-auto md:mx-0">
               <ChefHat size={32} />
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-4">Master Bakers</h3>
            <p className="text-stone-500 font-light leading-relaxed">Hand-kneaded dough and slow fermentation process for that perfect artisan crust.</p>
          </div>
          <div className="text-center md:text-left">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center text-orange-600 shadow-xl mb-8 mx-auto md:mx-0">
               <Sparkles size={32} />
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-4">Pure Ingredients</h3>
            <p className="text-stone-500 font-light leading-relaxed">No preservatives. Just real butter, organic flour, and farm-fresh seasonal fruits.</p>
          </div>
          <div className="text-center md:text-left">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center text-orange-600 shadow-xl mb-8 mx-auto md:mx-0">
               <Truck size={32} />
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-4">Safe Delivery</h3>
            <p className="text-stone-500 font-light leading-relaxed">Specially designed shock-proof packaging to ensure your cake arrives in perfect shape.</p>
          </div>
        </div>
      </section>

      {/* === Daily Menu Grid === */}
      <section id="menu" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">Fresh from the <span className="italic text-orange-600">Oven</span></h2>
            <div className="flex items-center justify-center gap-4">
              <span className="h-px w-12 bg-stone-200"></span>
              <p className="text-stone-400 text-xs font-bold uppercase tracking-widest">Available Daily until sold out</p>
              <span className="h-px w-12 bg-stone-200"></span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: "Butter Croissant", price: "₹149", img: "https://images.pexels.com/photos/2135/food-france-morning-breakfast.jpg?auto=compress&cs=tinysrgb&w=600" },
              { name: "Pain au Chocolat", price: "₹179", img: "http://gateaux-et-delices.com/wp-content/uploads/2016/02/Pains-au-chocolat-express1-1.jpg" },
              { name: "Sourdough Loaf", price: "₹280", img: "https://images.pexels.com/photos/1586947/pexels-photo-1586947.jpeg?auto=compress&cs=tinysrgb&w=600" },
              { name: "Berry Danish", price: "₹199", img: "https://images.pexels.com/photos/461060/pexels-photo-461060.jpeg?auto=compress&cs=tinysrgb&w=600" }
            ].map((item, i) => (
              <div key={i} className="group">
                <div className="h-72 rounded-[24px] overflow-hidden mb-6 relative">
                  <img src={item.img} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <button onClick={() => setIsFormOpen(true)} className="absolute bottom-4 right-4 bg-white text-stone-900 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-orange-600 hover:text-white">
                    <Plus size={20} />
                  </button>
                </div>
                <h3 className="font-bold text-stone-900 mb-1">{item.name}</h3>
                <p className="text-orange-600 font-black">{item.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Footer Section === */}
      <footer id="contact" className="bg-stone-950 text-white pt-32 pb-12 px-6 lg:px-12 mt-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-16 mb-24 border-b border-stone-800 pb-24">
          <div className="md:col-span-5">
            <span className="font-serif text-3xl font-black uppercase mb-8 block">Crust & <span className="text-orange-500">Cream</span></span>
            <p className="text-stone-400 font-light text-lg leading-relaxed max-w-sm mb-10">We believe in real food. No chemicals, no compromises. Just the best bakes in town delivered to your doorstep.</p>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-stone-900 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors cursor-pointer border border-stone-800"><Instagram size={20}/></div>
              <div className="w-12 h-12 bg-stone-900 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors cursor-pointer border border-stone-800"><MessageCircle size={20}/></div>
            </div>
          </div>

          <div className="md:col-span-4">
             <h4 className="text-white font-black uppercase tracking-[0.2em] text-[10px] mb-10">Directory</h4>
             <ul className="space-y-4 text-stone-400 text-sm font-medium">
               <li className="hover:text-orange-500 cursor-pointer flex items-center gap-2"><ChevronRight size={14}/> Custom Birthday Cakes</li>
               <li className="hover:text-orange-500 cursor-pointer flex items-center gap-2"><ChevronRight size={14}/> Wedding Cake Consultation</li>
               <li className="hover:text-orange-500 cursor-pointer flex items-center gap-2"><ChevronRight size={14}/> Bulk Party Orders</li>
               <li className="hover:text-orange-500 cursor-pointer flex items-center gap-2"><ChevronRight size={14}/> Daily Artisan Breads</li>
             </ul>
          </div>

          <div className="md:col-span-3">
             <h4 className="text-white font-black uppercase tracking-[0.2em] text-[10px] mb-10">Bake House</h4>
             <div className="space-y-6 text-stone-400 text-sm">
               <p className="flex gap-3"><MapPin className="text-orange-500 shrink-0" size={18}/> Shop 42, Bakery Lane, City Center</p>
               <p className="flex gap-3"><Phone className="text-orange-500 shrink-0" size={18}/> +91 98765 43210</p>
               <p className="flex gap-3"><Clock className="text-orange-500 shrink-0" size={18}/> Open Daily: 8 AM - 11 PM</p>
             </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center flex flex-col md:flex-row justify-between items-center text-stone-600 text-[10px] font-black tracking-widest uppercase">
          <p>© {new Date().getFullYear()} Crust & Cream. All Rights Reserved.</p>
          <div className="mt-4 md:mt-0 flex gap-6">
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
          </div>
        </div>
      </footer>

      {/* === Modern Cake Order Popup === */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsFormOpen(false)} className="absolute inset-0 bg-stone-900/80 backdrop-blur-md"></motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-white p-8 md:p-14 rounded-[40px] shadow-2xl relative w-full max-w-xl z-10 border border-orange-50">
              <button onClick={() => setIsFormOpen(false)} className="absolute top-8 right-8 text-stone-400 hover:text-stone-900 transition-colors bg-stone-50 p-2 rounded-full"><X size={24}/></button>
              
              <div className="flex items-center gap-4 mb-8">
                <div className="bg-orange-100 p-3 rounded-2xl text-orange-600"><Sparkles size={24} /></div>
                <h3 className="font-serif text-3xl text-stone-900 font-black">Place Your Order</h3>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); alert("Order Received! Our team will call you back."); setIsFormOpen(false); }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-2">Your Name</label>
                    <input type="text" required className="w-full bg-stone-50 border border-stone-200 px-6 py-4 rounded-2xl focus:outline-none focus:border-orange-500 font-medium" placeholder="E.g. John Doe" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-2">Phone Number</label>
                    <input type="tel" required className="w-full bg-stone-50 border border-stone-200 px-6 py-4 rounded-2xl focus:outline-none focus:border-orange-500 font-medium" placeholder="+91 00000 00000" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-2">Select Category</label>
                  <select className="w-full bg-stone-50 border border-stone-200 px-6 py-4 rounded-2xl focus:outline-none focus:border-orange-500 font-medium text-stone-600">
                    <option>Custom Birthday Cake</option>
                    <option>Wedding Cake Consultation</option>
                    <option>Assorted Pastry Box</option>
                    <option>Artisan Bread Hamper</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-2">Special Notes (Flavour/Theme)</label>
                  <textarea className="w-full bg-stone-50 border border-stone-200 px-6 py-4 rounded-2xl focus:outline-none focus:border-orange-500 font-medium h-24" placeholder="E.g. Eggless, Dark Chocolate with Raspberries..."></textarea>
                </div>

                <button type="submit" className="w-full bg-orange-600 text-white font-black tracking-[0.2em] uppercase py-6 rounded-[24px] hover:bg-stone-900 transition-all mt-4 shadow-2xl shadow-orange-100">
                  Send Inquiry Now
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BasicBakeryLanding;